import axios from "axios";
import {makeApolloClientCall} from "../../../api/makeApolloApiCall.js";
import {SDNO_COMPANY, SDNO_CONTACTS_QUERY} from "../data-subscription/queries.js";
import {useCallback, useEffect, useMemo, useState} from "react";
import {
    useFetchAllSubscriptions,
    useFetchCategories,
    useFetchGeoFamilyIds
} from "../data-subscription/DataSubscription.hooks.jsx";
import {flattenGroupedSubscriptions, flattenSubcatFromCategories, mapKeyAccountDetails} from "./utils.js";
import {toast} from "react-toastify";
import {getAccessToken} from "../../../api/getAccessToken.js";


export const useFetchCompaniesWithSpinsContacts = (subscriptions) => {
    const [loading, setIsLoading] = useState(true)
    const [companiesWithSpinsContacts, setCompaniesWithSpinsContacts] = useState([]);
    const fetchCompaniesWithSpinsContacts = useCallback(async () => {
        const approvalRules = (await axios.get(`${process.env.REACT_APP_RIS_API_URL}/data-subscriptions/retailers/approval-rules`, {
            headers: {
                'Content-Type': 'application/json',
                'authorization': `Bearer ${getAccessToken()}`
            }
        })).data
        const geoFamilyIds = new Set(subscriptions.map(subscription => subscription.sec_mkt_key.toString()))
        const companyIds = approvalRules
            .filter(approvalRule => geoFamilyIds.has(approvalRule?.geo_family_id?.toString()))
            .map(approvalRule => approvalRule.company_id)

        const sdnoCompanies = companyIds.reduce((acc, companyId) => {
            return acc + SDNO_COMPANY
                .replace("$sdnoCompanyId", `"${companyId}"`)
                .replace("$alias", `company_${companyId}`)
        }, "")
        const sdnoQuery = `query SdnoCompany { ${sdnoCompanies} }`

        return makeApolloClientCall(sdnoQuery, false).then(result => {
            const flattenedCompanies = Object.keys(result).flatMap(key => {
                return result[key]?.edges.map(edge => {
                    const node = edge.node
                    const approvalRule = approvalRules.find(approvalRule => approvalRule?.company_id === node.id)
                    return {...node, geoFamilyId: approvalRule?.geo_family_id}
                })
            })
            setCompaniesWithSpinsContacts(flattenedCompanies)
        }).catch(error => {
            console.log(`There was an error in fetching the spins owner.. ${error}`)
            setIsLoading(false)
        }).finally(() => setIsLoading(false))
    }, [subscriptions])

    useEffect(() => {
        if (subscriptions.length) {
            fetchCompaniesWithSpinsContacts().then();
        }
    }, [subscriptions])

    return {companiesWithSpinsContacts, loading}
};

export const useFetchAutoApprovedRetailerIds = () => {
    const [loading, setIsLoading] = useState(true)
    const [autoApprovedRetailerIds, setAutoApprovedRetailerIds] = useState([]);

    const fetchAutoApprovedRetailerIds = async () => {
        axios.get(`${process.env.REACT_APP_RIS_API_URL}/data-subscriptions/retailers/auto-approved-ids`, {
            headers: {
                'Content-Type': 'application/json',
                'authorization': `Bearer ${getAccessToken()}`
            }
        })
            .then(r => setAutoApprovedRetailerIds(r.data))
            .catch(error => console.log(`Error in fetching the auto approved retailer ids. ${error}`))
            .finally(() => setIsLoading(false));
    }

    useEffect(() => {
        fetchAutoApprovedRetailerIds().then();
    }, [])

    return {autoApprovedRetailerIds, loading}
}

/*
For REFACTORING?

Once we have all the needed columns for the Key Accounts Approval page, we can move all this logic to the backend instead of doing all this data gymnastics here.
It **should** be much easier to aggregate and connect all the data in the backend then send it to the frontend as its own row than what is happening right now.

This hook's purpose will be to fetch those rows, but for now it's here for aggregating all the rows that will be on the Key Accounts Approval table
 */
export function useKeyAccounts() {
    const {subscriptions, loading: subscriptionsLoading, fetchAllSubscriptions} = useFetchAllSubscriptions()
    const {categories, isCategoriesLoading: categoriesLoading} = useFetchCategories()
    const {geoFamilyIds, isGeoFamilyIdsLoading: geoFamilyIdsLoading} = useFetchGeoFamilyIds()
    const {autoApprovedRetailerIds, loading: autoApprovedRetailerIdsLoading} = useFetchAutoApprovedRetailerIds()
    const {
        companiesWithSpinsContacts,
        loading: companiesWithSpinsContactsLoading
    } = useFetchCompaniesWithSpinsContacts(subscriptions)

    const rows = useMemo(() => {
        if (subscriptions.length && categories.length && companiesWithSpinsContacts.length) {
            const categoriesWithListSubCat = flattenSubcatFromCategories(categories)
            const filteredSubscriptions = autoApprovedRetailerIds.length ?
                subscriptions.filter(subscription => !autoApprovedRetailerIds.includes(Number.parseInt(subscription.sec_mkt_key))) : subscriptions
            const groupedSubscriptions = mapKeyAccountDetails(
                filteredSubscriptions, categoriesWithListSubCat, companiesWithSpinsContacts
            )
            return flattenGroupedSubscriptions(
                groupedSubscriptions, geoFamilyIds, companiesWithSpinsContacts
            );
        }
        return []
    }, [subscriptions, categories, companiesWithSpinsContacts, autoApprovedRetailerIds, geoFamilyIds])

    return {
        rows,
        loading: subscriptionsLoading || categoriesLoading || geoFamilyIdsLoading || autoApprovedRetailerIdsLoading || companiesWithSpinsContactsLoading,
        fetchAllSubscriptions
    }
}

export function useKeyAccountsConfig() {
    const [keyAccountConfig, setKeyAccountConfig] = useState([]);
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_RIS_API_URL}/data-subscriptions/retailers/key-accounts-config`, {
            headers: {
                'Content-Type': 'application/json',
                'authorization': `Bearer ${getAccessToken()}`
            }
        }).then(({data}) => {
            setKeyAccountConfig(data)
        })
    }, []);
    const updateKeyAccountConfig = (value) => {
        setKeyAccountConfig(value);
        axios.post(`${process.env.REACT_APP_RIS_API_URL}/data-subscriptions/retailers/key-accounts-config`, value, {
            headers: {
                'Content-Type': 'application/json',
                'authorization': `Bearer ${getAccessToken()}`
            },
        }).then(({data}) => toast.success(`Updated key account config list: ${data.map(d => d.displayName).join(', ')}`))
    }
    return {keyAccountConfig, setKeyAccountConfig, updateKeyAccountConfig}
}

export const useCompanyContacts = (retailerSfid) => {
    const [companyContacts, setCompanyContacts] = useState([])

    const fetchCompanyContacts = useCallback(async () => {
        makeApolloClientCall(SDNO_CONTACTS_QUERY, true, {companySfid: retailerSfid}).then((response) => {
            setCompanyContacts(response.sdnoContacts.edges.map(edge => {
                const fullName = `${edge.node.fname ?? ""} ${edge.node.lname}`
                return {displayName: fullName, value: fullName}
            }))
        })
    }, [retailerSfid])

    useEffect(() => {
        fetchCompanyContacts()
    }, [fetchCompanyContacts]);
    return companyContacts
}
